import { appConfig } from '@/common/config/app.config';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getSendLogMetadata } from './send-log-metadata';
import { currentReportedExtensionVersion } from '@common/services/reported-extension-version';
import { ATTEMPT_LOG_ENDPOINT, flushParkedAttemptLogs, parkAttemptLog, trackFlush } from './deferred-attempt-logs';

// Function to get access token from client storage
async function getAccessToken(): Promise<string> {
  return localStorage.getItem('bt-token') || "";
}

/**
 * The authoritative persisted token (background hub in extension contexts), read lazily so this
 * helper keeps no static dependency on the auth module. Empty string when none is stored.
 */
async function getPersistedAccessToken(): Promise<string> {
  try {
    const { authService } = await import('@/common/bt-auth/auth-service');
    return (await authService.getPersistedToken()) ?? "";
  } catch {
    return "";
  }
}

function isUnauthorized(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

// H15: keep an attempt-log row that a 401 burst would otherwise lose (see deferred-attempt-logs.ts).
function parkIfAttemptLog(endpoint: string, body: unknown): void {
  if (endpoint !== ATTEMPT_LOG_ENDPOINT || typeof body !== 'object' || body === null) return;
  parkAttemptLog({ ...(body as Record<string, unknown>) });
}

// Redeliver parked rows after any successful call, off the caller's critical path. The log endpoint
// itself is skipped, and a redelivery is sent with `redelivery` set so a rejected row is re-queued by
// flushParkedAttemptLogs (with its attempt count) rather than parked again as a new row.
function deliverParkedLogs(endpoint: string): void {
  if (endpoint === ATTEMPT_LOG_ENDPOINT) return;
  trackFlush(flushParkedAttemptLogs((body) => request('post', ATTEMPT_LOG_ENDPOINT, body, undefined, true)));
}

// Generic function for making API calls
export async function apiCall<T>(
  method: 'get' | 'post' | 'put' | 'delete',
  endpoint: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  return request<T>(method, endpoint, data, config, false);
}

async function request<T>(
  method: 'get' | 'post' | 'put' | 'delete',
  endpoint: string,
  data: any,
  config: AxiosRequestConfig | undefined,
  redelivery: boolean
): Promise<T> {

  try {
    let token = "";
    const axiosConfig: AxiosRequestConfig = {
      method,
      url: `${appConfig.API_ENDPOINT}${endpoint}`,
      headers: {
        'Content-Type': 'application/json',
        ...(config?.headers || {}),
      },
      ...config,
    };


    token = await getAccessToken();
    // #569: an empty localStorage 'bt-token' sends `Bearer ` -> the backend answers `jwt malformed`
    // (401) on every /actions call while the feathers client keeps working. Fall back to the
    // authoritative persisted token before sending.
    if (!token) token = await getPersistedAccessToken();
    // Add Authorization header — headers literal was assigned above.
    if (!axiosConfig.headers) {
        axiosConfig.headers = {};
    }
    axiosConfig.headers['Authorization'] = `Bearer ${token}`;

    // #ext-version-logging: stamp the extension/gateway build version onto message-log actions so
    // duplicate-send analysis can correlate by version going forward (persisted in messagelogs
    // metadata.extensionVersion). Guarded — a no-op outside the extension/gateway (e.g. the web app,
    // where `chrome` is undefined and these log endpoints aren't called anyway).
    const body = data || {};
    if (/cmd=(create-message-attempt-log|delete-user-message-and-create-log|create-failed-message-log)/.test(endpoint)) {
      const v = currentReportedExtensionVersion();
      if (v) body.extensionVersion = v;
      // Per-message send metadata (group/community, message-type, failure, addressing) resolved by
      // the send thunk and stashed by messageId — stamp it onto the same log-action bodies so it
      // persists in messagelogs.metadata. Same guarded, no-throw pattern as extensionVersion; only
      // defined fields are copied so the row isn't polluted with undefined keys.
      try {
        const meta = getSendLogMetadata((body as any)?.messageId);
        if (meta) {
          for (const [k, val] of Object.entries(meta)) {
            if (val !== undefined && (body as any)[k] === undefined) (body as any)[k] = val;
          }
        }
      } catch { /* metadata stamping must never break the send */ }
    }
    axiosConfig.data = body;

    console.log(`API_ENDPOINT:`);
    console.log(`${appConfig.API_ENDPOINT}${endpoint}`);

    try {
      const response: AxiosResponse<T> = await axios(axiosConfig);
      if (!redelivery) deliverParkedLogs(endpoint);
      return response.data;
    } catch (firstError) {
      if (!isUnauthorized(firstError)) throw firstError;
      // #569: re-read the persisted token ONCE and retry only if it is a different, non-empty
      // credential than the one just rejected (a re-auth since the request was built). Same token
      // again would just 401 twice.
      const fresh = await getPersistedAccessToken();
      if (!fresh || fresh === token) {
        if (!redelivery) parkIfAttemptLog(endpoint, axiosConfig.data);
        throw firstError;
      }
      axiosConfig.headers['Authorization'] = `Bearer ${fresh}`;
      try {
        const retried: AxiosResponse<T> = await axios(axiosConfig);
        if (!redelivery) deliverParkedLogs(endpoint);
        return retried.data;
      } catch (retryError) {
        if (isUnauthorized(retryError) && !redelivery) parkIfAttemptLog(endpoint, axiosConfig.data);
        throw retryError;
      }
    }
  } catch (error) {
    console.error(`Error in API call to ${endpoint}:`, error);
    throw error;
  }
}
